import { create } from 'zustand';
import { fetchLearningResourceCategories } from '@/service/api/learning';

interface LearningCategoryState {
  /** 学习资源分类列表 */
  categories: Api.Learning.CategoryResponse[];
  /** 是否正在加载中 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;

  /** 获取学习资源分类列表 (支持 force 强制刷新) */
  fetchCategories: (force?: boolean) => Promise<Api.Learning.CategoryResponse[]>;
  /** 根据 code 或 id 获取分类详情对象 */
  getCategoryByCode: (code?: string) => Api.Learning.CategoryResponse | undefined;
  /** 根据 id 或 code 获取分类详情对象 */
  getCategoryById: (id?: string) => Api.Learning.CategoryResponse | undefined;
  /** 根据 code 或 id 获取分类名称 */
  getCategoryNameByCode: (code?: string, fallback?: string) => string;
  /** 根据 id 或 code 获取分类名称 */
  getCategoryNameById: (id?: string, fallback?: string) => string;
  /** 清除错误信息 */
  clearError: () => void;
}

let fetchCategoriesPromise: Promise<Api.Learning.CategoryResponse[]> | null = null;

export const useLearningCategoryStore = create<LearningCategoryState>((set, get) => ({
  categories: [],
  loading: false,
  error: null,

  fetchCategories: async (force = false) => {
    const { categories } = get();
    if (!force && categories.length > 0) {
      return categories;
    }
    if (fetchCategoriesPromise) {
      return fetchCategoriesPromise;
    }

    set({ loading: true, error: null });
    fetchCategoriesPromise = fetchLearningResourceCategories()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        set({ categories: list, loading: false, error: null });
        return list;
      })
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : '获取学习资源分类失败';
        set({ loading: false, error: errorMessage });
        throw err;
      })
      .finally(() => {
        fetchCategoriesPromise = null;
      });

    return fetchCategoriesPromise;
  },

  getCategoryByCode: (code) => {
    if (!code) return undefined;
    const { categories } = get();
    return categories.find((item) => item.code === code || item.id === code);
  },

  getCategoryById: (id) => {
    if (!id) return undefined;
    const { categories } = get();
    return categories.find((item) => item.id === id || item.code === id);
  },

  getCategoryNameByCode: (code, fallback = '') => {
    if (!code) return fallback;
    const { categories } = get();
    const target = categories.find((item) => item.code === code || item.id === code);
    return target?.name || fallback || code;
  },

  getCategoryNameById: (id, fallback = '') => {
    if (!id) return fallback;
    const { categories } = get();
    const target = categories.find((item) => item.id === id || item.code === id);
    return target?.name || fallback || id;
  },

  clearError: () => set({ error: null }),
}));
